<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                {{ editingPreparation ? "Edit Asset Preparation" : "Add New Asset Preparation" }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit" ref="form">
                  <v-row class="pa-1">
                    <!-- Preparation Date -->
                    <v-col md="6" cols="12">
                      <v-date-input v-model="preparation_date.value.value" label="Preparation Date *" density="compact"
                        prepend-icon="" prepend-inner-icon="mdi-calendar" variant="outlined" bg-color="grey-lighten-4"
                        :error-messages="preparation_date.errorMessage.value" autocomplete="off"
                        clearable></v-date-input>
                    </v-col>

                    <!-- Remarks -->
                    <v-col md="6" cols="12">
                      <v-textarea v-model="remarks.value.value" label="Remarks" variant="outlined" density="compact"
                        bg-color="grey-lighten-4" rows="2" :error-messages="remarks.errorMessage.value"></v-textarea>
                    </v-col>
                  </v-row>

                  <!-- Assets Selection Section -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <v-card variant="outlined" class="pa-4">
                        <v-card-title class="pa-2">
                          <span class="text-h6">Select Assets for Preparation</span>
                        </v-card-title>
                        <v-card-text>
                          <!-- Asset Search -->
                          <v-row class="pb-2">
                            <v-col md="6" cols="12">
                              <v-autocomplete v-model="selectedAssetId" label="Search and Add Asset" variant="outlined"
                                density="compact" bg-color="grey-lighten-4" :items="availableAssets"
                                :item-title="(item) => `${item.code} - ${item.name}`" item-value="id"
                                :loading="assetLoading" return-object prepend-inner-icon="mdi-magnify" clearable>
                                <template v-slot:item="{ props, item }">
                                  <v-list-item v-bind="props">
                                    <template v-slot:title>
                                      <div>
                                        <strong>{{ item.raw.code }}</strong> - {{ item.raw.name }}
                                      </div>
                                      <div class="text-caption text-grey">
                                        Category: {{ item.raw.asset_category?.name }} |
                                        Location: {{ item.raw.asset_location?.name }} |
                                        Status: {{ item.raw.asset_status?.name }}
                                      </div>
                                    </template>
                                  </v-list-item>
                                </template>
                              </v-autocomplete>
                            </v-col>
                            <v-col md="2" cols="12">
                              <v-btn color="primary" block @click="addAssetToPreparation" :disabled="!selectedAssetId"
                                prepend-icon="mdi-plus">
                                Add Asset
                              </v-btn>
                            </v-col>
                          </v-row>

                          <!-- Selected Assets Table -->
                          <v-data-table :headers="assetHeaders" :items="preparationAssets" :items-per-page="5"
                            density="compact" class="elevation-1">
                            <template v-slot:item.code="{ item }">
                              <strong>{{ item.code }}</strong>
                            </template>
                            <template v-slot:item.currentLocation="{ item }">
                              <v-chip size="small" color="blue" variant="tonal">
                                {{ item.asset_location?.name }}
                              </v-chip>
                            </template>
                            <template v-slot:item.status="{ item }">
                              <v-chip size="small" color="orange" variant="tonal">
                                {{ item.asset_status?.name }}
                              </v-chip>
                            </template>
                            <template v-slot:item.preparation_cost="{ item }">
                              <v-text-field v-model.number="item.preparation_cost" type="number" step="0.01" min="0"
                                density="compact" variant="outlined" hide-details placeholder="0.00"
                                style="width: 150px;"></v-text-field>
                            </template>
                            <template v-slot:item.remarks="{ item }">
                              <v-text-field v-model="item.remarks" density="compact" variant="outlined" hide-details
                                placeholder="Add remarks..." style="min-width: 200px;"></v-text-field>
                            </template>
                            <template v-slot:item.actions="{ item }">
                              <v-btn icon="mdi-delete" size="small" color="error" variant="text"
                                @click="removeAssetFromPreparation(item)"></v-btn>
                            </template>
                            <template v-slot:no-data>
                              <div class="text-center py-4">
                                <v-icon size="48" color="grey">mdi-package-variant</v-icon>
                                <p class="text-grey">No assets added yet</p>
                              </div>
                            </template>
                          </v-data-table>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Action Buttons -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <div class="d-flex justify-end" style="gap: 12px;">
                        <v-btn
                          class="bg-grey-darken-1 me-2"
                          variant="flat"
                          type="button"
                          :loading="saving && currentAction === 0"
                          :disabled="saving"
                          @click="submitForm(0)"
                        >
                          DRAFT
                        </v-btn>
                        <v-btn
                          color="primary"
                          type="button"
                          :loading="saving && currentAction === 1"
                          :disabled="saving && currentAction !== 1"
                          prepend-icon="mdi-content-save"
                          @click="submitForm(1)"
                        >
                          {{ editingPreparation ? "Update & Post" : "POST" }}
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, computed, watch } from "vue";
import { toast } from "vue3-toastify";
import { getAssetRegistrationsList } from "@/store/api";
import Axios from "axios";

export default {
  components: {},
  data() {
    return {
      panel: [0],
      assetLoading: false,
      saving: false,
      currentAction: null,
      selectedAssetId: null,
      preparationAssets: [],
      allAssets: [],
      assetHeaders: [
        { title: "Code", key: "code", align: "left", sortable: false },
        { title: "Name", key: "name", align: "left", sortable: false },
        { title: "Category", key: "asset_category.name", align: "left", sortable: false },
        { title: "Current Location", key: "currentLocation", align: "left", sortable: false },
        { title: "Status", key: "status", align: "left", sortable: false },
        { title: "Preparation Cost", key: "preparation_cost", align: "right", sortable: false },
        { title: "Remarks", key: "remarks", align: "left", sortable: false },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
      currentStatus: 1,
    };
  },
  computed: {
    editingPreparation() {
      return this.$store.getters.getEditingAssetPreparation;
    },
    availableAssets() {
      // Filter assets that can be prepared (not already selected)
      return this.allAssets.filter((asset) => {
        // Don't show already selected assets
        return !this.preparationAssets.some((selected) => selected.id === asset.id);
      });
    },
  },
  watch: {
    editingPreparation: {
      handler(newVal) {
        if (newVal && newVal.id) {
          // Always fetch fresh data for editing to ensure all relationships are loaded
          this.loadEditDataById(newVal.id);
        }
      },
      immediate: true,
    },
  },
  created() {
    this.loadAssets();
  },
  setup() {
    const store = useStore();

    const validationSchema = yup.object({
      preparation_date: yup.string().required("Preparation date is required"),
      remarks: yup.string().nullable(),
    });

    const { handleSubmit, resetForm } = useForm({
      validationSchema,
    });

    const preparation_date = useField("preparation_date");
    const remarks = useField("remarks");

    return {
      preparation_date,
      remarks,
      handleSubmit,
      resetForm,
    };
  },
  methods: {
    async submitForm(status) {
      // status: 0 => DRAFT, 1 => POST (default)
      this.currentStatus = typeof status !== "undefined" ? status : 1;
      this.currentAction = this.currentStatus;
      try {
        await this.submit();
      } finally {
        this.currentAction = null;
      }
    },
    async submit() {
      try {
        // Check if at least one asset is selected
        if (this.preparationAssets.length === 0) {
          toast.error("Please add at least one asset to prepare");
          return;
        }

        // Validate and submit
        await this.handleSubmit(async (values) => {
          this.saving = true;
          const preparationData = {
            preparation_date: values.preparation_date,
            remarks: values.remarks || null,
            asset_preparation_master_id: this.editingPreparation?.id || null,
            status: this.currentStatus ?? 1,
            assets: this.preparationAssets.map((asset) => ({
              asset_register_id: asset.id,
              preparation_cost: asset.preparation_cost || 0,
              remarks: asset.remarks || null,
            })),
          };

          try {
            const response = await this.$store.dispatch("saveUpdateAssetPreparation", preparationData);
            if (response.data.success) {
              toast.success(response.data.message || "Asset preparation saved successfully");
              this.$store.commit("UNSET_EDITING_ASSET_PREPARATION");
              this.$router.push({ name: "preparation-records" });
            } else {
              toast.error(response.data.message || "Failed to save asset preparation");
            }
          } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save asset preparation");
          } finally {
            this.saving = false;
          }
        })();
      } catch (error) {
        console.error("Validation error:", error);
      }
    },
    async loadAssets() {
      this.assetLoading = true;
      try {
        const response = await Axios.get(getAssetRegistrationsList, {
          params: {},
        });
        this.allAssets = response.data.data || [];
      } catch (error) {
        console.error("Error loading assets:", error);
        this.allAssets = [];
      } finally {
        this.assetLoading = false;
      }
    },
    async loadEditDataById(id) {
      try {
        // Ensure assets are loaded first
        if (this.allAssets.length === 0) {
          await this.loadAssets();
        }

        const response = await this.$store.dispatch("getAssetPreparationById", id);
        console.log("Preparation data response:", response);
        if (response && response.data) {
          this.loadEditData(response.data);
        }
      } catch (error) {
        console.error("Error loading preparation data:", error);
        toast.error("Failed to load preparation data");
      }
    },
    loadEditData(preparation) {
      if (preparation) {
        console.log("Loading edit data:", preparation);
        this.preparation_date.value.value = preparation.preparation_date;
        this.remarks.value.value = preparation.remarks || "";
        this.currentStatus = typeof preparation.status !== "undefined" ? preparation.status : 1;

        // Load existing preparation assets with full details
        if (preparation.details && preparation.details.length > 0) {
          console.log("Preparation details:", preparation.details);
          this.preparationAssets = preparation.details.map((detail) => {
            const asset = detail.assetRegister || this.allAssets.find(a => a.id === detail.asset_register_id);
            if (asset) {
              return {
                ...asset,
                preparation_cost: detail.preparation_cost || 0,
                remarks: detail.remarks || "",
              };
            }
            return null;
          }).filter(Boolean);
          console.log("Loaded preparation assets:", this.preparationAssets);
        }
      }
    },
    addAssetToPreparation() {
      if (!this.selectedAssetId) {
        toast.error("Please select an asset");
        return;
      }

      // Check if asset is already added
      if (this.preparationAssets.some((asset) => asset.id === this.selectedAssetId.id)) {
        toast.warning("This asset is already added");
        return;
      }

      // Add asset with default values
      const asset = {
        ...this.selectedAssetId,
        preparation_cost: 0,
        remarks: "",
      };

      this.preparationAssets.push(asset);
      this.selectedAssetId = null;
      toast.success("Asset added successfully");
    },
    removeAssetFromPreparation(asset) {
      const index = this.preparationAssets.findIndex((a) => a.id === asset.id);
      if (index > -1) {
        this.preparationAssets.splice(index, 1);
        toast.success("Asset removed");
      }
    },
    goBack() {
      this.$store.commit("UNSET_EDITING_ASSET_PREPARATION");
      this.$router.push({ name: "preparation-records" });
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
